import { useState, useRef } from "react";
import { Upload, X, Film, Image, FileText, Loader2, AlertTriangle, Youtube } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
}

export default function FileUpload({ onUpload, accept = "image/*", uploadType = "general", label = "Upload File", currentUrl = "" }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(currentUrl || "");
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState(0);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const xhrRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setFileName(file.name);
    setFileSize(file.size);
    setProgress(0);

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File too large (${formatFileSize(file.size)}). Maximum is 500MB. For large videos, please upload to YouTube and paste the URL below.`);
      setPreview("");
      return;
    }

    // Show local preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("video/")) {
      setPreview("video");
    } else {
      setPreview("file");
    }

    // Upload with XMLHttpRequest for progress tracking
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_type", uploadType);

    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const pct = Math.round((event.loaded / event.total) * 100);
        setProgress(pct);
      }
    };

    xhr.onload = () => {
      setUploading(false);
      xhrRef.current = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          const fileUrl = `${API_BASE}${data.url}`;
          setPreview(fileUrl);
          setProgress(100);
          if (onUpload) onUpload(fileUrl, data);
        } catch (err) {
          setError("Upload succeeded but response was invalid.");
        }
      } else {
        try {
          const errData = JSON.parse(xhr.responseText);
          setError(errData.detail || "Upload failed.");
        } catch (err) {
          setError(`Upload failed (${xhr.status}). Try a smaller file or use YouTube URL.`);
        }
        setPreview("");
      }
    };

    xhr.onerror = () => {
      setUploading(false);
      xhrRef.current = null;
      setError("Upload failed. Check your internet connection and try again.");
      setPreview("");
    };

    xhr.ontimeout = () => {
      setUploading(false);
      xhrRef.current = null;
      setError("Upload timed out. File may be too large. Try a YouTube URL instead.");
      setPreview("");
    };

    xhr.open("POST", `${API_BASE}/api/upload`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.timeout = 600000; // 10 minutes
    xhr.send(formData);
  };

  const cancelUpload = () => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    setUploading(false);
    setProgress(0);
    setPreview("");
    setFileName("");
    setError("");
  };

  const clearFile = () => {
    setPreview("");
    setFileName("");
    setFileSize(0);
    setError("");
    setProgress(0);
    if (inputRef.current) inputRef.current.value = "";
    if (onUpload) onUpload("", null);
  };

  const isImage = preview && (preview.startsWith("data:image") || preview.match(/\.(jpg|jpeg|png|gif|webp)/i) || (preview.startsWith("http") && !preview.includes("video")));

  return (
    <div data-testid="file-upload-component">
      <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider mb-1 block">{label}</label>

      {/* Preview */}
      {preview && preview !== "video" && preview !== "file" && isImage && (
        <div className="relative mb-2 aspect-video w-full max-w-md bg-slate-200 overflow-hidden">
          <img src={preview} alt="Preview" className="w-full h-full object-cover" />
          <button type="button" onClick={clearFile} className="absolute top-1 right-1 bg-black/60 text-white p-1"><X size={14} /></button>
        </div>
      )}

      {preview === "video" && !uploading && (
        <div className="mb-2 bg-slate-800 text-white p-3 flex items-center gap-2 max-w-md">
          <Film size={18} className="text-[#0284C7]" />
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium truncate block">{fileName}</span>
            {fileSize > 0 && <span className="text-[10px] text-slate-400">{formatFileSize(fileSize)}</span>}
          </div>
          <button type="button" onClick={clearFile} className="ml-auto text-white/60"><X size={14} /></button>
        </div>
      )}

      {preview === "file" && !uploading && (
        <div className="mb-2 bg-slate-100 p-3 flex items-center gap-2 max-w-md">
          <FileText size={18} className="text-[#0284C7]" />
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium truncate block admin-text">{fileName}</span>
            {fileSize > 0 && <span className="text-[10px] admin-text-secondary">{formatFileSize(fileSize)}</span>}
          </div>
          <button type="button" onClick={clearFile} className="ml-auto text-slate-400"><X size={14} /></button>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="mb-3 max-w-md" data-testid="upload-progress">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Loader2 size={14} className="text-[#0284C7] animate-spin" />
              <span className="text-xs font-medium admin-text">Uploading {fileName}...</span>
            </div>
            <button type="button" onClick={cancelUpload} className="text-xs text-red-500 font-bold">Cancel</button>
          </div>
          <div className="h-2 bg-slate-200 w-full overflow-hidden">
            <div
              className="h-full bg-[#0284C7] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] admin-text-secondary">{formatFileSize(fileSize)}</span>
            <span className="text-xs font-bold text-[#0284C7]">{progress}%</span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-2 bg-red-50 border border-red-200 p-3 max-w-md flex gap-2" data-testid="upload-error">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-red-700 font-medium">{error}</p>
            {fileSize > MAX_FILE_SIZE && (
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-amber-600">
                <Youtube size={12} />
                <span>Tip: Upload your video to YouTube (unlisted) and paste the URL below</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Button */}
      {!uploading && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-2 bg-[#0284C7] text-white font-bold py-2.5 px-4 text-xs transition-none hover:bg-[#0369A1]"
          >
            <Upload size={14} /> {preview ? "Change File" : "Choose File"}
          </button>
          {fileName && !error && (
            <span className="text-xs admin-text-secondary truncate max-w-[200px]">{fileName}</span>
          )}
        </div>
      )}

      <input ref={inputRef} type="file" accept={accept} onChange={handleFileSelect} className="hidden" />

      {/* File size guidance */}
      {!fileName && !uploading && accept?.includes("video") && (
        <p className="text-[10px] admin-text-secondary mt-1.5">
          Max 500MB. For larger videos, upload to YouTube and paste URL below.
        </p>
      )}
    </div>
  );
}
